
"use client";

import { BRAND, withBase, getCountry } from '../lib/brand';
import { TOKENS_CSS, ContourMark, displayType, DISPLAY_EM_CSS } from '../lib/fieldGuide';
import { HOME_CONTENT } from '../lib/homeContent';

import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { AutoComplete, Input } from "antd";
import type { InputRef } from "antd";
import { SearchOutlined, SettingOutlined, CloseOutlined } from "@ant-design/icons";
import { FaCompass, FaCaravan, FaCampground, FaLeaf, FaHiking, FaHome, FaMapMarkerAlt, FaRoute, FaMapSigns } from "react-icons/fa";
import { GiCampingTent } from "react-icons/gi";
import { useCaPreview } from '../lib/caPreview';
import CustomRadiusSlider from "./CustomRadiusSlider";
import FeatureIcon from "./FeatureIcon";
import type { Feature } from "../types";
import { AssistantMessage } from "../types/assistant";

// Hero copy lookups — AU strings preserved exactly; escapeto resolves per
// country (path segment) at render time.
const HERO_POSSESSIVE: Record<string, React.ReactNode> = {
  AU: <>Australia&apos;s</>,
  US: <>America&apos;s</>,
  CA: <>Canada&apos;s</>,
  NZ: <>New Zealand&apos;s</>,
};
// Plain-string possessive form for i18n interpolation (the {possessive}
// placeholder in messages). Kept alongside HERO_POSSESSIVE (ReactNode, still
// used by the camping-australia byte-identical branch). Includes AR/CL, which
// the ReactNode/PLACE maps predate.
const HERO_POSSESSIVE_STR: Record<string, string> = {
  AU: "Australia's",
  US: "America's",
  CA: "Canada's",
  NZ: "New Zealand's",
  AR: "Argentina's",
  CL: "Chile's",
};
const HERO_PLACE: Record<string, string> = {
  AU: 'Australia',
  US: 'America',
  CA: 'Canada',
  NZ: 'New Zealand',
  AR: 'Argentina',
  CL: 'Chile',
};

// Map each internal type value (also compared in page.tsx logic — never
// translate the VALUE) to its `tabs` message key.
const TAB_KEY: Record<string, string> = {
  'All': 'all',
  'Caravan Parks': 'caravanParks',
  'Camping Sites': 'campingSites',
  'Free Camping': 'freeCamping',
  'Activities': 'activities',
  'Accommodation': 'accommodation',
};

// Popular searches / routes / region chips now live in per-country content
// maps (app/lib/homeContent.ts). AU strings there are byte-identical to the
// constants that used to be defined here.

const TAB_ICONS: Record<string, React.ReactNode> = {
  All: <FaCompass />,
  "Caravan Parks": <FaCaravan />,
  "Camping Sites": <FaCampground />,
  "Free Camping": <FaLeaf />,
  "Activities": <FaHiking />,
  "Accommodation": <FaHome />,
};

/**
 * Set the possessive ("America's", "Chile's"…) in the headline's italic accent
 * face without adding markup to the message catalogues. Falls back to the plain
 * string whenever a locale reorders the sentence so the needle isn't present.
 */
const emphasise = (sentence: string, needle?: string): React.ReactNode => {
  if (!needle) return sentence;
  const at = sentence.indexOf(needle);
  if (at < 0) return sentence;
  return (
    <>
      {sentence.slice(0, at)}
      <em>{needle}</em>
      {sentence.slice(at + needle.length)}
    </>
  );
};

/**
 * Filter chips reuse the campsite feature icon vocabulary (topCampsiteFeatures
 * via FeatureIcon) that the detail pages already use, so a "Hot Showers" chip
 * here carries the same glyph it does on a listing. `text-current` keeps the
 * icon in the chip's own colour instead of FeatureIcon's per-category palette,
 * which would fight the chip's selected state.
 */
const FilterGlyph: React.FC<{ name: string }> = ({ name }) => (
  <FeatureIcon feature={{ id: 0, desc: name } as Feature} size="sm" className="text-current" />
);

interface HeroSearchProps {
  searchQuery: string;
  selectedType: string;
  showFilters: boolean;
  selectedFeatures: string[];
  radius: number;
  options: { value: string }[];
  isSearching: boolean;
  onLocationSearch: (value: string) => void;
  onLocationSelect: (value: string) => void;
  onLocationChange: (value: string) => void;
  onTypeChange: (type: string) => void;
  onToggleFilters: () => void;
  onToggleFeature: (feature: string) => void;
  onClearFilters: () => void;
  onRadiusChange: (value: number) => void;
  onSearch: () => void;
  onPressEnter: () => void;
  onPickRegion?: (regionName: string) => void;
  onPickRoute?: (route: string) => void;
  /** Run a plain geocoded location search (escapeto region chips — no Region nodes outside AU). */
  onPickLocation?: (location: string) => void;
  features: string[];
  assistantMessages: AssistantMessage[];
  assistantSuggestions: string[];
  assistantIsOpen: boolean;
  assistantHasUnread: boolean;
  assistantIsProcessing: boolean;
  onToggleAssistant: () => void;
  onSendAssistant: (message: string) => void;
  /** Open the explorer's trip planner and scroll it into view. */
  onPlanTrip?: () => void;
}

const baseTypeOptions = [
  { value: "All", label: "All" },
  { value: "Caravan Parks", label: "Caravan Parks" },
  { value: "Camping Sites", label: "Camping Sites" },
  { value: "Free Camping", label: "Free Camping" },
  { value: "Activities", label: "Activities" },
  { value: "Accommodation", label: "Accommodation" },
];

const HeroSearch: React.FC<HeroSearchProps> = ({
  searchQuery,
  selectedType,
  showFilters,
  selectedFeatures,
  radius,
  options,
  isSearching,
  onLocationSearch,
  onLocationSelect,
  onLocationChange,
  onTypeChange,
  onToggleFilters,
  onToggleFeature,
  onClearFilters,
  onRadiusChange,
  onSearch,
  onPressEnter,
  onPickRegion,
  onPickRoute,
  onPickLocation,
  features,
  assistantMessages,
  assistantSuggestions,
  assistantIsOpen,
  assistantHasUnread,
  assistantIsProcessing,
  onToggleAssistant,
  onSendAssistant,
  onPlanTrip,
}) => {
  const t = useTranslations('hero');
  const tc = useTranslations('common');
  const caPreview = useCaPreview();
  const tt = useTranslations('tabs');
  // Translate a type tab's display label; the VALUE stays English (page.tsx
  // logic compares against it). Unknown/custom types fall back to their value.
  const tabLabel = (value: string): string => (TAB_KEY[value] ? tt(TAB_KEY[value]) : value);

  const [showAllFeatureGroups, setShowAllFeatureGroups] = useState(false);
  const searchInputRef = useRef<InputRef>(null);

  // Which shortcut (if any) produced the current search. The quick links stay
  // on screen while a search is active, so without this there'd be no feedback
  // about which one you picked.
  const matchesQuery = useCallback(
    (candidate: string) =>
      searchQuery.trim().toLowerCase() === candidate.trim().toLowerCase(),
    [searchQuery]
  );

  // Keyboard shortcut: Cmd/Ctrl+K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const typeOptions = useMemo(() => {
    if (
      !selectedType ||
      baseTypeOptions.find((option) => option.value === selectedType)
    ) {
      return baseTypeOptions;
    }
    return [...baseTypeOptions, { value: selectedType, label: selectedType }];
  }, [selectedType]);

  const featureGroups = useMemo(() => {
    const groups = [
      {
        title: "Essentials & Amenities",
        items: [
          "Powered Sites",
          "Unpowered Sites",
          "Toilets",
          "Hot Showers",
          "Showers",
          "Drinking Water",
          "Camp Kitchen",
          "Laundry",
          "Dump Point",
          "Accessible Facilities",
          "Campfires Allowed",
        ],
      },
      {
        title: "Comfort & Services",
        items: [
          "Cabins",
          "Glamping",
          "WiFi",
          "BBQ",
          "Playground",
          "Swimming Pool",
          "Family Friendly",
          "Pet Friendly",
          "On-site Cafe",
          "Firewood Available",
        ],
      },
      {
        title: "Outdoor Adventures",
        items: [
          "River Access",
          "Beach Access",
          "Lake Access",
          "Boat Ramp",
          "Fishing",
          "Hiking Trails",
          "Cycling Trails",
          "4WD Access",
          "Horse Riding",
          "Stargazing",
        ],
      },
      {
        title: "Points of Interest",
        items: [
          "Lookout",
          "Waterfall",
          "Scenic Drive",
          "National Park Visitor Centre",
          "Wildlife Park",
          "Botanic Gardens",
          "Historic Site",
          "Heritage Walk",
          "Museum",
          "Art Gallery",
          "Winery",
          "Brewery",
          "Distillery",
          "Farm Gate",
          "Local Market",
        ],
      },
    ];

    const assigned = new Set<string>();
    const resolvedGroups = groups
      .map((group) => ({
        title: group.title,
        items: group.items.filter((item) => {
          if (!features.includes(item)) return false;
          assigned.add(item);
          return true;
        }),
      }))
      .filter((group) => group.items.length > 0);

    const remaining = features.filter((feature) => !assigned.has(feature));
    if (remaining.length > 0) {
      resolvedGroups.push({ title: "More", items: remaining });
    }

    return resolvedGroups;
  }, [features]);

  const groupsToRender = showAllFeatureGroups
    ? featureGroups
    : featureGroups.slice(0, 3);

  useEffect(() => {
    if (!showFilters) {
      setShowAllFeatureGroups(false);
    }
  }, [showFilters]);

  // Location-specific home content (USER DIRECTION: "the regions and the
  // background image will be location specific"). On camping-australia
  // getCountry() is always AU, so this resolves to the pre-existing AU content.
  const content = HOME_CONTENT[getCountry()];

  // The feature filters drawer. The legacy hero shows it inside its console;
  // ca_preview shows it under the store hero (never swapping the hero out).
  const filtersDrawer = (
    <div className="fg-filters">
      <div className="fg-filters-head">
        <div>
          <p className="fg-quick-label">{t('refineResults')}</p>
          <h2 className="fg-filters-title">{t('filterByFeatures')}</h2>
        </div>
        <button type="button" onClick={onClearFilters} className="fg-filters-clear">
          {tc('clearAll')}
        </button>
      </div>

      {/* i18n TODO (phase 2): feature group titles and the individual
          feature chip names below are a controlled vocabulary that
          doubles as SEARCH FILTER VALUES (page.tsx matches on them),
          so they are deliberately left untranslated for phase 1 —
          translating the labels here without a value/label split
          would break filtering. */}
      <div className="fg-filter-groups">
        {groupsToRender.map((group) => (
          <div key={group.title}>
            <p className="fg-quick-label">{group.title}</p>
            <div className="fg-tags fg-tags--filters">
              {group.items.map((feature) => {
                const isActive = selectedFeatures.includes(feature);
                return (
                  <button
                    key={feature}
                    type="button"
                    onClick={() => onToggleFeature(feature)}
                    className={`fg-tag${isActive ? " is-on" : ""}`}
                  >
                    <FilterGlyph name={feature} />
                    {feature}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {featureGroups.length > 2 && (
        <button
          type="button"
          onClick={() => setShowAllFeatureGroups((prev) => !prev)}
          className="fg-filters-more"
        >
          {showAllFeatureGroups ? t('showFewerFeatures') : t('showAllFeatures')}
        </button>
      )}

      <div className="fg-radius">
        <p className="fg-quick-label">{t('searchRadius')}</p>
        <CustomRadiusSlider
          value={radius}
          onChange={onRadiusChange}
          min={5}
          max={200}
          step={5}
        />
      </div>
    </div>
  );

  // ca_preview: the store's hero composition (theme-aaa aaa-home-hero +
  // the Campsite Explorer door): copy on a charcoal grade at the left, the
  // photo's subject clear at the right, one search field with the store's
  // blue button, and the store's chips. The type tabs live on the map's own
  // chips. Opening Filters opens the feature drawer under the search row; it
  // used to swap this whole hero back to the legacy console (and, with no
  // .ca-xh on the page, the store breadcrumb disappeared too).
  if (caPreview) {
    return (
      <section className="ca-xh" aria-labelledby="ca-xh-h">
        {/* The planner's own photograph (the open road at Uluru), not the
            store homepage's planner promo, so landing here feels like
            arriving somewhere rather than a repeated section. */}
        {content.heroImage && (
          <Image
            src={withBase('/images/ca-planner-hero.webp')}
            alt=""
            fill
            priority
            quality={85}
            sizes="100vw"
            className="ca-xh__img"
          />
        )}
        <div className="ca-xh__shade" aria-hidden="true" />
        <div className="ca-xh__in">
          <p className="ca-xh__eyebrow">Campsite Explorer · free trip planner</p>
          <h1 id="ca-xh-h" className="ca-xh__h">Pick the road. We&rsquo;ll find the camps.</h1>
          <p className="ca-xh__p">
            Campgrounds, caravan parks and free camps in every state. Search a place, plan a route and save the spots you like.
          </p>
          <div className="ca-xh__form" role="search">
            <FaMapMarkerAlt className="ca-xh__pin" aria-hidden="true" />
            <AutoComplete
              options={options}
              value={searchQuery}
              onSelect={onLocationSelect}
              onSearch={onLocationSearch}
              className="ca-xh__ac"
              style={{ height: 'auto' }}
            >
              <Input
                ref={searchInputRef}
                autoComplete="off"
                aria-label="Where do you want to camp?"
                placeholder="Town, park or region, e.g. Grampians"
                onPressEnter={onPressEnter}
                onChange={(event) => onLocationChange(event.target.value)}
                className="ca-xh__input"
              />
            </AutoComplete>
            <button type="button" onClick={onSearch} disabled={isSearching} className="ca-xh__go">
              {isSearching ? 'Searching…' : 'Find campsites'}
            </button>
          </div>
          <div className="ca-xh__row">
            {onPlanTrip && (
              <button type="button" onClick={onPlanTrip} className="ca-xh__btn">
                <FaRoute aria-hidden="true" /> Plan a road trip
              </button>
            )}
            <button
              type="button"
              onClick={onToggleFilters}
              aria-expanded={showFilters}
              aria-controls="ca-xh-filters"
              className={`ca-xh__btn ca-xh__btn--ghost${showFilters ? ' is-on' : ''}`}
            >
              <SettingOutlined /> Filters{selectedFeatures.length > 0 ? ` (${selectedFeatures.length})` : ''}
            </button>
          </div>
          {showFilters && (
            <div id="ca-xh-filters" className="ca-xh__filters">
              <style dangerouslySetInnerHTML={{ __html: CA_FILTERS_CSS }} />
              {filtersDrawer}
              <div className="ca-xh__filters-foot">
                <button type="button" onClick={onToggleFilters} className="ca-xh__filters-close">
                  Close
                </button>
                <button type="button" onClick={onSearch} disabled={isSearching} className="ca-xh__go ca-xh__filters-go">
                  {isSearching ? 'Searching…' : searchQuery.trim() ? 'Show results' : 'Search all of Australia'}
                </button>
              </div>
            </div>
          )}
          {onPickRegion && (
            <div className="ca-xh__chips">
              <span className="ca-xh__gh">Popular regions</span>
              <ul role="list">
                {content.popularRegions.slice(0, 7).map((r) => (
                  <li key={r.searchQuery}>
                    <button
                      type="button"
                      aria-pressed={matchesQuery(`region:${r.searchQuery}`)}
                      onClick={() => onPickRegion(r.searchQuery)}
                    >
                      {r.label}
                    </button>
                  </li>
                ))}
                <li><a href={withBase('/regions')}>All regions →</a></li>
              </ul>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="fg-hero">
      {/* Scoped "field guide" stylesheet. Kept as a plain style tag (same
          approach as the owner portal) so the hero owns its own design tokens
          without leaking them into the rest of the app, and so nothing here
          depends on Tailwind class ordering inside the Shopify app proxy. */}
      {/* dangerouslySetInnerHTML, not a text child: React HTML-escapes a
          <style> child during SSR (apostrophes and angle brackets in the
          comments) and then re-renders it raw on the client, which trips a
          hydration mismatch. Same form layout.tsx uses for its critical CSS. */}
      <style dangerouslySetInnerHTML={{ __html: FIELD_GUIDE_CSS }} />

      {/* ── Backdrop: photo (or per-country gradient), warm scrim, contour
             linework, film grain. Stops short of the section's bottom edge on
             desktop so the search panel can overhang onto the page. ── */}
      <div className="fg-backdrop" aria-hidden="true">
        {content.heroImage ? (
          <Image
            src={withBase(content.heroImage)}
            alt=""
            fill
            priority
            quality={90}
            sizes="100vw"
            className="fg-photo"
          />
        ) : (
          // No licensed hero photo wired for this country yet — scenery-toned
          // gradient fallback (see homeContent.ts) instead of the AU photo.
          <div className="fg-photo" style={{ background: content.heroGradient }} />
        )}
        <div className="fg-scrim" />
        <div className="fg-warmth" />
        <ContourMark className="fg-contour" />
        <div className="fg-grain" />
      </div>

      <div className="fg-inner">
        <header className="fg-head">
          <p className="fg-eyebrow">
            <GiCampingTent className="fg-eyebrow-mark" aria-hidden="true" />
            {/* camping-australia keeps the original ReactNode possessive so its
                SSR output stays byte-identical; escapeto resolves via i18n. */}
            <span className="fg-eyebrow-long">
              {BRAND.key === 'camping-australia'
                ? <>{HERO_POSSESSIVE[getCountry()]} Camping Search Engine</>
                : t('badge', { possessive: HERO_POSSESSIVE_STR[getCountry()], place: HERO_PLACE[getCountry()] })}
            </span>
            <span className="fg-eyebrow-short">{t('badgeShort')}</span>
            <span className="fg-eyebrow-rule" />
          </p>

          <h1 className="fg-title">
            {BRAND.key === 'camping-australia'
              ? <>Discover <em>{HERO_POSSESSIVE[getCountry()]}</em> best camping spots</>
              : emphasise(
                  t('heading', {
                    possessive: HERO_POSSESSIVE_STR[getCountry()],
                    place: HERO_PLACE[getCountry()],
                  }),
                  HERO_POSSESSIVE_STR[getCountry()]
                )}
          </h1>

          <p className="fg-sub">{t('subheading')}</p>

          <ul className="fg-legend">
            {BRAND.key === 'camping-australia' ? (
              <>
                <li>8,000+ campsites</li>
                <li>1,200+ free camps</li>
                <li>All of Australia</li>
              </>
            ) : (
              <>
                <li>{t('statsCampsites')}</li>
                <li>{t('statsFreeCamps')}</li>
                <li>{t('statsAll', { place: HERO_PLACE[getCountry()] })}</li>
              </>
            )}
          </ul>
        </header>

        {/* ── The search console: a solid paper panel, not a glass card. ── */}
        <div className="fg-panel">
          <nav className="fg-tabs" aria-label={tc('filters')}>
            {typeOptions.map((option) => {
              const isActive = selectedType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onTypeChange(option.value)}
                  aria-pressed={isActive}
                  className={`fg-tab${isActive ? " is-active" : ""}`}
                >
                  {TAB_ICONS[option.value] && (
                    <span className="fg-tab-icon">{TAB_ICONS[option.value]}</span>
                  )}
                  {tabLabel(option.value)}
                </button>
              );
            })}
          </nav>

          <div className="fg-panel-body">
            <div className="fg-field">
              <div className="fg-field-input">
                <AutoComplete
                  options={options}
                  value={searchQuery}
                  onSelect={onLocationSelect}
                  onSearch={onLocationSearch}
                  className="fg-autocomplete"
                  style={{ height: 'auto' }}
                >
                  <Input
                    ref={searchInputRef}
                    size="large"
                    autoComplete="off"
                    prefix={<SearchOutlined className="fg-input-icon" />}
                    suffix={
                      <kbd className="fg-kbd">
                        <span suppressHydrationWarning>
                          {typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '⌘' : 'Ctrl'}
                        </span>
                        K
                      </kbd>
                    }
                    placeholder={t('placeholder')}
                    onPressEnter={onPressEnter}
                    onChange={(event) => onLocationChange(event.target.value)}
                    className="fg-input"
                  />
                </AutoComplete>
              </div>

              <div className="fg-actions">
                <button
                  type="button"
                  onClick={onToggleFilters}
                  className={`fg-btn fg-btn--ghost${showFilters || selectedFeatures.length > 0 ? " is-on" : ""}`}
                >
                  <SettingOutlined />
                  <span>{tc('filters')}</span>
                  {selectedFeatures.length > 0 && (
                    <span className="fg-btn-count">{selectedFeatures.length}</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={onSearch}
                  disabled={isSearching}
                  className="fg-btn fg-btn--go"
                >
                  {isSearching ? <span className="fg-spinner" /> : <SearchOutlined />}
                  <span>{tc('search')}</span>
                </button>

                {/* The trip planner sits under the hero, collapsed — this is
                    the visible way in (and where /trip-planner now lands). */}
                {onPlanTrip && (
                  <button
                    type="button"
                    onClick={onPlanTrip}
                    className="fg-btn fg-btn--plan"
                  >
                    <FaRoute aria-hidden="true" />
                    <span>{t('planTrip')}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Popular regions — camping-australia: one-tap region:<name> graph
                search; escapeto: plain geocoded LOCATION search (no Region nodes
                outside AU). "See all regions" only where the region browse UI
                exists (BRAND.showRegions). */}
            {(BRAND.showRegions ? onPickRegion : onPickLocation) && (
              <div className="fg-quick">
                <span className="fg-quick-label">{t('popularRegions')}</span>
                <div className="fg-tags">
                  {content.popularRegions.map((r) => {
                    // camping-australia runs regions as `region:<name>`;
                    // escapeto runs them as a plain location search.
                    const isActive = matchesQuery(
                      BRAND.showRegions ? `region:${r.searchQuery}` : r.searchQuery
                    );
                    return (
                      <button
                        key={r.searchQuery}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => {
                          if (BRAND.showRegions && onPickRegion) onPickRegion(r.searchQuery);
                          else onPickLocation?.(r.searchQuery);
                        }}
                        className={`fg-tag${isActive ? " is-on" : ""}`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                  {BRAND.showRegions && (
                    <a href={withBase("/regions")} className="fg-tag fg-tag--all">
                      <FaMapSigns aria-hidden="true" />
                      See all regions
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Popular places & routes — plain index links rather than a third
                row of chips, so the three entry points read as a hierarchy
                instead of one undifferentiated field of pills.

                These stay put once a search is running. They used to be hidden
                the moment the input had a value, which meant picking a region
                (which writes `region:<name>` into the input) made popular
                places and routes vanish — so the one action most likely to be
                followed by "actually, show me somewhere else" was also the one
                that removed the shortcuts for doing it. */}
            <div className="fg-index">
              <p className="fg-index-row">
                <span className="fg-index-label">
                  <FaMapMarkerAlt className="fg-index-mark" aria-hidden="true" />
                  {t('popular')}
                </span>
                {content.popularSearches.map((place) => {
                  const isActive = matchesQuery(place);
                  return (
                    <button
                      key={place}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => {
                        onLocationChange(place);
                        onLocationSelect(place);
                      }}
                      className={`fg-index-link${isActive ? " is-on" : ""}`}
                    >
                      {place}
                    </button>
                  );
                })}
              </p>
              <p className="fg-index-row">
                <span className="fg-index-label fg-index-label--route">
                  <FaRoute className="fg-index-mark" aria-hidden="true" />
                  {t('routes')}
                </span>
                {content.popularRoutes.map((route) => {
                  const isActive = matchesQuery(route.query) || matchesQuery(route.label);
                  return (
                    <button
                      key={route.label}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => {
                        onLocationChange(route.query);
                        if (onPickRoute) onPickRoute(route.query);
                        else onLocationSelect(route.query);
                      }}
                      className={`fg-index-link fg-index-link--route${isActive ? " is-on" : ""}`}
                    >
                      {route.label}
                    </button>
                  );
                })}
              </p>
            </div>

            {/* Active filter tags — shown when filters are selected but panel is closed */}
            {selectedFeatures.length > 0 && !showFilters && (
              <div className="fg-active">
                <span className="fg-quick-label">{t('filtersLabel')}</span>
                {selectedFeatures.slice(0, 3).map((feature) => (
                  <span key={feature} className="fg-active-tag">
                    {feature}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFeature(feature);
                      }}
                      aria-label={`Remove ${feature} filter`}
                    >
                      <CloseOutlined />
                    </button>
                  </span>
                ))}
                {selectedFeatures.length > 3 && (
                  <button type="button" onClick={onToggleFilters} className="fg-active-more">
                    {t('moreCount', { count: selectedFeatures.length - 3 })}
                  </button>
                )}
                <button type="button" onClick={onClearFilters} className="fg-active-clear">
                  {tc('clearAll')}
                </button>
              </div>
            )}

            {showFilters && filtersDrawer}
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * "Field guide" hero styles.
 *
 * Layout and type do the work the old gradient-and-glass treatment was doing:
 * a full-bleed photo, heavy display type (per brand, see fieldGuide.displayType)
 * over the site's Work Sans, a tab strip instead of six identical pills, and a
 * solid paper search console that overhangs the photo's bottom edge.
 */
const FIELD_GUIDE_CSS = `
.fg-hero{
${TOKENS_CSS}

  position:relative;
  isolation:isolate;
  /* Full-bleed out of the 1280px <main> container — a hero that stops short of
     the viewport edges reads as a widget, not a page. Same escape hatch the
     mega menu uses; body has overflow-x:hidden so 100vw is safe. */
  width:100vw;
  margin-left:calc(50% - 50vw);
  background:var(--fg-page);
  color:var(--fg-paper);
  padding-bottom:0;
}

/* ── Backdrop ───────────────────────────────────────────────────────────── */
.fg-backdrop{position:absolute;inset:0;overflow:hidden;z-index:-1}
.fg-photo{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:32% center}
.fg-scrim{
  position:absolute;inset:0;
  background:
    linear-gradient(97deg,rgba(12,20,34,.92) 0%,rgba(12,20,34,.78) 34%,rgba(14,24,38,.38) 68%,rgba(14,24,38,.52) 100%),
    linear-gradient(to top,rgba(11,19,32,.72) 0%,rgba(11,19,32,0) 42%);
}
/* Grades the photo toward the palette instead of fighting it with slate/indigo. */
.fg-warmth{
  position:absolute;inset:0;
  background:radial-gradient(90% 70% at 88% 6%,rgba(124,196,245,.30) 0%,rgba(124,196,245,0) 60%);
  mix-blend-mode:soft-light;
}
.fg-contour{
  position:absolute;right:-60px;bottom:-70px;width:min(46vw,560px);height:auto;
  color:var(--fg-paper);opacity:.16;pointer-events:none;display:none;
}
.fg-grain{
  position:absolute;inset:0;opacity:.16;mix-blend-mode:overlay;pointer-events:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E");
}

/* ── Layout ─────────────────────────────────────────────────────────────── */
.fg-inner{
  position:relative;
  max-width:1180px;margin:0 auto;
  padding:24px 16px 20px;
  display:flex;flex-direction:column;gap:18px;
}

/* ── Headline block ─────────────────────────────────────────────────────── */
/* Kept deliberately short: the hero + console used to fill the whole first
   screen at 1400x900, so not one campsite showed without scrolling. */
.fg-head{max-width:46rem;display:flex;flex-direction:column;gap:10px}
.fg-eyebrow{
  display:flex;align-items:center;gap:10px;margin:0;
  font-family:"Work Sans",sans-serif;font-size:10.5px;font-weight:600;
  letter-spacing:.18em;text-transform:uppercase;color:rgba(242,246,251,.82);
}
.fg-eyebrow-mark{flex:none;font-size:14px;color:var(--fg-blue-lt)}
.fg-eyebrow-short{display:none}
.fg-eyebrow-rule{flex:1 1 auto;height:1px;background:linear-gradient(to right,rgba(242,246,251,.55),rgba(242,246,251,0));max-width:180px}
.fg-title{
  margin:0;
  ${displayType({ w: 800, ls: '-.022em', size: 'clamp(1.665rem,3.78vw,2.61rem)', lh: 1.04 }, { w: 600, ls: '-.02em', size: 'clamp(1.85rem,4.2vw,2.9rem)', lh: 1.02 })}
  color:#ffffff;
  text-wrap:balance;
  text-shadow:0 2px 24px rgba(10,17,29,.45);
}
.fg-title em{
  ${DISPLAY_EM_CSS};color:var(--fg-blue-lt);
  padding-right:.06em;
}
.fg-sub{
  margin:0;max-width:34rem;
  font-family:"Work Sans",sans-serif;font-size:15px;line-height:1.55;
  color:rgba(242,246,251,.84);
  /* The photo brightens hard behind the copy (open water on the AU shot), so
     the small text carries its own shadow rather than the scrim being pushed
     dark enough to flatten the image. */
  text-shadow:0 1px 14px rgba(9,16,28,.65);
}
.fg-legend{
  margin:2px 0 0;padding:0;list-style:none;
  display:flex;flex-wrap:wrap;align-items:center;gap:0;
  font-family:"Work Sans",sans-serif;font-size:10.5px;font-weight:600;
  letter-spacing:.12em;text-transform:uppercase;color:rgba(242,246,251,.72);
  font-variant-numeric:tabular-nums;
  text-shadow:0 1px 12px rgba(9,16,28,.7);
}
.fg-legend li{padding:0 14px;border-left:1px solid rgba(242,246,251,.28);white-space:nowrap}
.fg-legend li:first-child{padding-left:0;border-left:0}

/* ── Search console ─────────────────────────────────────────────────────── */
.fg-panel{
  position:relative;
  background:linear-gradient(176deg,#fbfcfe 0%,var(--fg-paper) 46%,var(--fg-paper-2) 100%);
  border:1px solid rgba(22,35,58,.22);
  border-radius:14px;
  color:var(--fg-ink);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.8),
    0 2px 0 rgba(22,35,58,.10),
    0 34px 60px -28px rgba(9,16,28,.75);
  overflow:hidden;
}
/* Paper grain, so the panel reads as printed stock rather than frosted glass. */
.fg-panel::before{
  content:"";position:absolute;inset:0;pointer-events:none;opacity:.045;mix-blend-mode:multiply;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='p'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23p)'/%3E%3C/svg%3E");
}
.fg-panel > *{position:relative}

/* Tab strip — an index rule, not six identical pills. */
.fg-tabs{
  display:flex;gap:2px;align-items:stretch;
  padding:0 8px;
  border-bottom:1px solid var(--fg-line);
  background:linear-gradient(180deg,rgba(255,255,255,.5),rgba(255,255,255,0));
  overflow-x:auto;scrollbar-width:none;
}
.fg-tabs::-webkit-scrollbar{display:none}
.fg-tab{
  position:relative;flex:none;
  display:inline-flex;align-items:center;gap:7px;
  padding:13px 12px;border:0;background:none;cursor:pointer;
  font-family:"Work Sans",sans-serif;font-size:12.5px;font-weight:600;
  letter-spacing:.01em;color:var(--fg-ink-mute);white-space:nowrap;
  transition:color .18s ease;
}
.fg-tab:hover{color:var(--fg-ink)}
.fg-tab.is-active{color:var(--fg-ink)}
.fg-tab::after{
  content:"";position:absolute;left:12px;right:12px;bottom:-1px;height:2px;
  background:var(--fg-blue);transform:scaleX(0);transform-origin:left;
  transition:transform .22s cubic-bezier(.2,.7,.3,1);
}
.fg-tab.is-active::after{transform:scaleX(1)}
.fg-tab-icon{display:inline-flex;font-size:12px;opacity:.55;transition:opacity .18s ease}
.fg-tab.is-active .fg-tab-icon{opacity:1;color:var(--fg-blue)}

.fg-panel-body{display:flex;flex-direction:column;gap:12px;padding:14px}

/* Input + actions */
.fg-field{display:flex;flex-direction:column;gap:10px}
.fg-field-input{flex:1 1 auto;min-width:0}
.fg-autocomplete{width:100%}
.fg-hero .fg-input.ant-input-affix-wrapper{
  height:46px;padding:0 14px;border-radius:9px;
  background:#ffffff;
  border:1px solid rgba(22,35,58,.24);
  box-shadow:inset 0 1px 2px rgba(22,35,58,.07);
  transition:border-color .18s ease,box-shadow .18s ease;
}
.fg-hero .fg-input.ant-input-affix-wrapper:hover{border-color:rgba(22,35,58,.4)}
.fg-hero .fg-input.ant-input-affix-wrapper-focused,
.fg-hero .fg-input.ant-input-affix-wrapper:focus-within{
  border-color:var(--fg-blue);
  box-shadow:0 0 0 3px rgba(26,104,209,.18);
}
.fg-hero .fg-input .ant-input{
  background:transparent;
  font-family:"Work Sans",sans-serif;font-size:15px;color:var(--fg-ink);
}
.fg-hero .fg-input .ant-input::placeholder{color:var(--fg-ink-mute);opacity:.85}
.fg-input-icon{color:var(--fg-ink-mute);font-size:15px}
.fg-kbd{
  display:none;align-items:center;gap:3px;
  padding:2px 6px;border:1px solid var(--fg-line);border-radius:4px;
  background:rgba(22,35,58,.05);
  font-family:"Work Sans",sans-serif;font-size:10px;font-weight:600;
  color:var(--fg-ink-mute);letter-spacing:.02em;
}

.fg-actions{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.fg-btn{
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  height:46px;padding:0 18px;border-radius:9px;cursor:pointer;white-space:nowrap;
  font-family:"Work Sans",sans-serif;font-size:12.5px;font-weight:700;
  letter-spacing:.07em;text-transform:uppercase;
  transition:background .18s ease,color .18s ease,border-color .18s ease,transform .12s ease,box-shadow .18s ease;
}
.fg-btn:active{transform:translateY(1px)}
.fg-btn--ghost{
  background:rgba(255,255,255,.6);
  border:1px solid rgba(22,35,58,.24);
  color:var(--fg-ink-soft);
}
.fg-btn--ghost:hover{border-color:var(--fg-ink);color:var(--fg-ink);background:#fff}
.fg-btn--ghost.is-on{background:var(--fg-ink);border-color:var(--fg-ink);color:var(--fg-paper)}
.fg-btn-count{
  display:inline-flex;align-items:center;justify-content:center;
  min-width:18px;height:18px;padding:0 5px;border-radius:9px;
  background:var(--fg-blue);color:#fff;font-size:10px;letter-spacing:0;
}
.fg-btn--go{
  background:var(--fg-blue);border:1px solid var(--fg-blue-dk);color:#ffffff;
  box-shadow:0 1px 0 rgba(255,255,255,.25) inset,0 8px 18px -8px rgba(21,88,176,.85);
}
.fg-btn--go:hover:not(:disabled){background:var(--fg-blue-dk)}
.fg-btn--go:disabled{background:var(--fg-ink-mute);border-color:var(--fg-ink-mute);cursor:not-allowed;box-shadow:none}
/* Plan a road trip — secondary to Search, but unmistakably a button. On
   phones it takes its own full-width row under Filters / Search. */
.fg-btn--plan{
  grid-column:1 / -1;height:42px;
  background:#ffffff;border:1px solid rgba(26,104,209,.45);color:var(--fg-blue);
}
.fg-btn--plan svg{flex:none;font-size:13px}
.fg-btn--plan:hover{background:var(--fg-blue);border-color:var(--fg-blue-dk);color:#ffffff}
.fg-spinner{
  width:15px;height:15px;border-radius:50%;
  border:2px solid rgba(255,255,255,.55);border-top-color:transparent;
  animation:fg-spin .7s linear infinite;
}
@keyframes fg-spin{to{transform:rotate(360deg)}}

/* Micro-label used by every secondary group (regions, filters, radius…). */
.fg-quick-label{
  margin:0;flex:none;
  font-family:"Work Sans",sans-serif;font-size:10px;font-weight:700;
  letter-spacing:.16em;text-transform:uppercase;color:var(--fg-ink-mute);
}

/* Region tags — squared like map labels, so they don't read as the same
   control as the popular/route links below. */
.fg-quick{display:flex;flex-direction:column;gap:8px}
.fg-tags{display:flex;flex-wrap:wrap;gap:6px}
.fg-tag{
  display:inline-flex;align-items:center;gap:7px;
  padding:6px 10px;border-radius:4px;cursor:pointer;
  border:1px solid rgba(22,35,58,.20);
  background:rgba(255,255,255,.55);
  font-family:"Work Sans",sans-serif;font-size:12.5px;font-weight:500;
  color:var(--fg-ink-soft);white-space:nowrap;text-decoration:none;
  transition:background .16s ease,color .16s ease,border-color .16s ease;
}
.fg-tag:hover{background:var(--fg-ink);border-color:var(--fg-ink);color:var(--fg-paper)}
.fg-tag.is-on{background:var(--fg-ink);border-color:var(--fg-ink);color:var(--fg-paper)}
.fg-tag svg{flex:none;opacity:.6;transition:opacity .16s ease}
.fg-tag:hover svg,.fg-tag.is-on svg{opacity:1}
.fg-tag--all{border-style:dashed;border-color:rgba(26,104,209,.5);color:var(--fg-blue);font-weight:600}
.fg-tag--all:hover{background:var(--fg-blue);border-color:var(--fg-blue);color:#fff}

/* Popular places + routes as an index, not a third pill row. */
.fg-index{display:flex;flex-direction:column;gap:6px;padding-top:2px;border-top:1px solid var(--fg-line)}
.fg-index-row{
  margin:0;display:flex;flex-wrap:wrap;align-items:center;gap:4px 12px;
  padding-top:6px;
}
.fg-index-label{
  display:inline-flex;align-items:center;gap:6px;
  font-family:"Work Sans",sans-serif;font-size:10px;font-weight:700;
  letter-spacing:.16em;text-transform:uppercase;color:var(--fg-ink-mute);
  min-width:74px;
}
.fg-index-mark{flex:none;font-size:12px;color:var(--fg-blue)}
.fg-index-link{
  padding:0;border:0;background:none;cursor:pointer;
  font-family:"Work Sans",sans-serif;font-size:13px;font-weight:500;
  color:var(--fg-ink-soft);
  text-decoration:underline;text-decoration-color:rgba(22,35,58,.22);
  text-underline-offset:4px;text-decoration-thickness:1px;
  transition:color .16s ease,text-decoration-color .16s ease;
}
.fg-index-link:hover{color:var(--fg-blue);text-decoration-color:var(--fg-blue)}
.fg-index-link.is-on{
  color:var(--fg-blue);font-weight:600;
  text-decoration-color:var(--fg-blue);text-decoration-thickness:2px;
}
.fg-index-link--route{font-weight:500}
/* The app-wide touch-target rule (button{min-height:44px;min-width:44px} in
   globals.css + layout.tsx) turned every region chip and index link into a
   44px-tall row, roughly doubling the console's height. Chips keep a
   comfortable 32px target; text links get 28px. */
.fg-hero .fg-tag{min-height:32px;min-width:0}
.fg-hero .fg-index-link{min-height:28px;min-width:0}

/* Active filter summary */
.fg-active{display:flex;flex-wrap:wrap;align-items:center;gap:8px}
.fg-active-tag{
  display:inline-flex;align-items:center;gap:6px;
  padding:5px 8px;border-radius:4px;background:rgba(22,35,58,.07);
  font-family:"Work Sans",sans-serif;font-size:11.5px;font-weight:500;color:var(--fg-ink);
}
.fg-active-tag button{border:0;background:none;cursor:pointer;color:var(--fg-ink-mute);font-size:9px;line-height:1;padding:0}
.fg-active-tag button:hover{color:var(--fg-blue)}
.fg-active-more,.fg-active-clear{
  border:0;background:none;cursor:pointer;
  font-family:"Work Sans",sans-serif;font-size:11px;font-weight:600;color:var(--fg-ink-mute);
}
.fg-active-clear:hover{color:var(--fg-blue)}

/* Filters drawer */
.fg-filters{
  border-top:1px solid var(--fg-line);margin-top:2px;padding-top:16px;
  display:flex;flex-direction:column;gap:16px;
}
.fg-filters-head{display:flex;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;gap:10px}
.fg-filters-title{
  margin:4px 0 0;
  ${displayType({ w: 700, ls: '-.018em', size: '19px' }, { w: 600, ls: '-.01em', size: '20px' })}
  color:var(--fg-ink);
}
.fg-filters-clear{
  border:0;background:none;cursor:pointer;
  font-family:"Work Sans",sans-serif;font-size:10px;font-weight:700;
  letter-spacing:.16em;text-transform:uppercase;color:var(--fg-ink-mute);
}
.fg-filters-clear:hover{color:var(--fg-blue)}
.fg-filter-groups{display:grid;gap:16px}
.fg-filter-groups > div{display:flex;flex-direction:column;gap:8px}
.fg-filters-more{
  align-self:flex-start;border:0;background:none;cursor:pointer;
  font-family:"Work Sans",sans-serif;font-size:10px;font-weight:700;
  letter-spacing:.16em;text-transform:uppercase;color:var(--fg-blue);
}
.fg-radius{
  border:1px solid var(--fg-line);border-radius:9px;
  background:rgba(255,255,255,.5);padding:14px;
  display:flex;flex-direction:column;gap:8px;
}

/* Keyboard focus is brand blue and consistent across every control here;
   the UA default ring read as a stray artefact on the paper panel. */
.fg-hero button:focus-visible,.fg-hero a:focus-visible{
  outline:2px solid var(--fg-blue);outline-offset:2px;border-radius:5px;
}
.fg-hero button:focus:not(:focus-visible){outline:none}

/* ── Entrance: one orchestrated load, not scattered micro-animations ────── */
@media (prefers-reduced-motion:no-preference){
  .fg-eyebrow,.fg-title,.fg-sub,.fg-legend,.fg-panel{animation:fg-rise .7s cubic-bezier(.2,.7,.25,1) both}
  .fg-eyebrow{animation-delay:.02s}
  .fg-title{animation-delay:.09s}
  .fg-sub{animation-delay:.17s}
  .fg-legend{animation-delay:.23s}
  .fg-panel{animation-delay:.29s}
  .fg-eyebrow-rule{animation:fg-draw .8s cubic-bezier(.2,.7,.25,1) .3s both}
}
@keyframes fg-rise{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
@keyframes fg-draw{from{transform:scaleX(0)}to{transform:scaleX(1)}}
.fg-eyebrow-rule{transform-origin:left}

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width:639px){
  .fg-eyebrow-long{display:none}
  .fg-eyebrow-short{display:inline}
}
@media (max-width:767px){
  /* Portrait crop: the horizontal scrim leaves only the dark left edge of the
     photo visible once the panel covers the lower half, so on narrow screens
     the crop moves to the subject and the scrim runs top-down instead. */
  .fg-photo{object-position:60% 40%}
  .fg-scrim{
    background:linear-gradient(180deg,rgba(12,20,34,.88) 0%,rgba(12,20,34,.6) 46%,rgba(12,20,34,.5) 100%);
  }
  /* Rule separators break badly once the legend wraps (a stray leading tick on
     the second line) — space them instead. */
  .fg-legend{gap:2px 14px}
  .fg-legend li{border-left:0;padding:0}
  /* Ten region tags wrap to six rows at 390px and bury the rest of the panel.
     Scroll them sideways, as the previous chip row did. The FILTER tags
     (.fg-tags--filters) still wrap — a 15-chip scroller would hide most of
     the options behind a swipe. */
  .fg-quick .fg-tags,.fg-index-row{
    flex-wrap:nowrap;overflow-x:auto;scrollbar-width:none;
    -webkit-overflow-scrolling:touch;
  }
  .fg-quick .fg-tags::-webkit-scrollbar,.fg-index-row::-webkit-scrollbar{display:none}
  .fg-quick .fg-tags > *,.fg-index-row > *{flex:none}
  .fg-index-label{
    position:sticky;left:0;z-index:1;min-width:0;padding-right:10px;
    background:linear-gradient(90deg,var(--fg-paper) 72%,rgba(242,246,251,0));
  }
}
@media (min-width:768px){
  .fg-contour{display:block}
  .fg-inner{padding:30px 32px 22px;gap:22px}
  .fg-head{gap:10px}
  .fg-sub{font-size:15.5px}
  .fg-kbd{display:inline-flex}
  .fg-field{flex-direction:row;align-items:center;gap:12px}
  .fg-actions{display:flex;gap:8px}
  .fg-btn--plan{height:46px}
  .fg-panel-body{padding:14px 18px 16px;gap:12px}
  .fg-tabs{padding:0 12px}
  .fg-tab{padding:12px 14px;font-size:13px}
  /* Regions: label inline with the chips (one row fewer), and lined up with
     the Popular / Routes index labels below. */
  .fg-quick{flex-direction:row;align-items:flex-start;gap:12px}
  .fg-quick > .fg-quick-label{min-width:118px;padding-top:8px}
  .fg-quick > .fg-tags{flex:1 1 auto;min-width:0}
  /* Label pulled out of the flow so a wrapped link lines up under the first
     link, not under the label. */
  .fg-index-row{position:relative;padding-left:130px;gap:2px 11px}
  .fg-index-label{position:absolute;left:0;top:6px;min-width:118px;height:28px}
  .fg-index-link{font-size:12.5px}
  .fg-tag{padding:5px 9px;font-size:12px}
  .fg-filter-groups{grid-template-columns:1fr 1fr;gap:18px 24px}
  /* The panel overhangs the photo onto the page — the hero stops behind it,
     which breaks the "card floating in a rectangle" look. */
  .fg-backdrop{bottom:44px}
}
@media (min-width:1024px){
  /* The stats legend rides on the eyebrow's line, so the subcopy gets the
     full width (one line) and the block loses a row. */
  .fg-head{
    max-width:none;display:grid;
    grid-template-columns:minmax(0,1fr) auto;column-gap:28px;row-gap:10px;
    align-items:center;
  }
  .fg-eyebrow{grid-row:1;grid-column:1}
  .fg-legend{grid-row:1;grid-column:2;justify-self:end;margin:0}
  .fg-title{grid-row:2;grid-column:1 / -1}
  .fg-sub{grid-row:3;grid-column:1 / -1;max-width:none}
  .fg-inner{padding-bottom:16px;gap:18px}
}
@media (min-width:1100px){
  .fg-inner{padding-top:28px}
}
`;

/**
 * ca_preview: the feature drawer as a white card under the store hero's
 * search row. It reuses the field-guide drawer markup (and its rules, from
 * FIELD_GUIDE_CSS) with the tokens scoped to the card, in the store's type.
 */
const CA_FILTERS_CSS = `${FIELD_GUIDE_CSS}
.ca-xh__in > .ca-xh__filters{max-width:880px}
.ca-xh__filters{
${TOKENS_CSS}
  margin-top:16px;padding:18px 20px 16px;border-radius:12px;
  background:#fff;color:var(--fg-ink);
  box-shadow:0 18px 40px -18px rgba(0,0,0,.55);
  max-height:min(62vh,560px);overflow-y:auto;overscroll-behavior:contain;
}
.ca-xh__filters .fg-filters{border-top:0;margin-top:0;padding-top:0}
.ca-xh__filters .fg-filters-title{font-family:"Poppins",system-ui,sans-serif;font-weight:700;font-size:18px;letter-spacing:-.01em}
/* aaa-skin turns .fg-tags into one-line scrolling rails on phones (right for
   region chips); filter chips wrap, so every option shows. */
html.ca-preview .ca-xh__filters .fg-tags{flex-wrap:wrap !important;overflow:visible !important;padding-right:0 !important;-webkit-mask-image:none;mask-image:none}
.ca-xh__filters .fg-filter-groups > div{min-width:0}
.ca-xh__filters .fg-tag{min-height:32px;min-width:0;background:#fff}
.ca-xh__filters .fg-tag.is-on,.ca-xh__filters .fg-tag:hover{background:#1c1e20;border-color:#1c1e20;color:#fff}
.ca-xh__filters button:focus-visible{outline:2px solid #1a68d1;outline-offset:2px}
.ca-xh__filters-foot{
  position:sticky;bottom:-16px;margin:14px -20px -16px;padding:12px 20px 14px;
  display:flex;justify-content:flex-end;align-items:center;gap:10px;
  background:#fff;border-top:1px solid rgba(22,35,58,.12);
}
.ca-xh__filters-close{
  min-height:44px;padding:0 16px;border:0;border-radius:6px;background:transparent;cursor:pointer;
  font:600 14px/1 "Poppins",system-ui,sans-serif;color:#1c1e20;
}
.ca-xh__filters-close:hover{background:#f3f0ee}
.ca-xh .ca-xh__filters-go{position:static;height:44px;min-height:44px;padding:0 20px;border-radius:6px}
.ca-xh__btn--ghost.is-on{background:rgba(255,255,255,.16)}
@media (max-width:640px){
  .ca-xh__filters{padding:16px 14px 14px;max-height:none}
  .ca-xh__filters-foot{margin:14px -14px -14px;padding:12px 14px;bottom:0}
  .ca-xh__filters-foot > *{flex:1}
}
`;

export default HeroSearch;
